import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StripeCardCollection from '../common/StripeCardCollection';

// Custom form field component - moved outside to prevent re-creation on each render
const FormField = ({ 
  label, 
  name, 
  type = 'text', 
  required = false, 
  placeholder = '', 
  options = [], 
  helpText = '',
  rows = null,
  value,
  onChange,
  fieldErrors,
  touchedFields,
  isSubmitting
}) => {
  const hasError = touchedFields[name] && fieldErrors[name];
  const isValid = touchedFields[name] && value && !fieldErrors[name];

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="font-semibold text-[#254e70] text-sm">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className="relative">
        {type === 'select' ? (
          <div className="relative">
            <select
              id={name}
              name={name}
              value={value}
              onChange={onChange}
              className={`w-full px-4 py-3.5 border-2 rounded-xl text-[#254e70] bg-white appearance-none cursor-pointer transition-all duration-200 focus:outline-none pr-12 ${
                hasError 
                  ? 'border-red-300 focus:border-red-500 bg-red-50' 
                  : isValid 
                  ? 'border-green-300 focus:border-green-500 bg-green-50' 
                  : 'border-gray-200 focus:border-[#9dacff] hover:border-gray-300'
              }`}
              required={required}
              disabled={isSubmitting}
            >
              <option value="">{placeholder || `Select ${label}`}</option>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {/* Custom dropdown arrow */}
            <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        ) : type === 'textarea' ? (
          <textarea
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            rows={rows || 4}
            className={`w-full px-4 py-3.5 border-2 rounded-xl text-[#254e70] resize-y transition-all duration-200 focus:outline-none ${
              hasError 
                ? 'border-red-300 focus:border-red-500 bg-red-50' 
                : isValid 
                ? 'border-green-300 focus:border-green-500 bg-green-50' 
                : 'border-gray-200 focus:border-[#9dacff] hover:border-gray-300'
            }`}
            required={required}
            disabled={isSubmitting}
          />
        ) : (
          <input
            type={type}
            id={name}
            name={name}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={`w-full px-4 py-3.5 border-2 rounded-xl text-[#254e70] transition-all duration-200 focus:outline-none ${
              hasError 
                ? 'border-red-300 focus:border-red-500 bg-red-50' 
                : isValid 
                ? 'border-green-300 focus:border-green-500 bg-green-50' 
                : 'border-gray-200 focus:border-[#9dacff] hover:border-gray-300'
            }`}
            required={required}
            disabled={isSubmitting}
          />
        )}
        
        {/* Validation icons */}
        {(hasError || isValid) && (
          <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
            {hasError ? (
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        )}
      </div>
      
      {/* Error message */}
      {hasError && (
        <p className="text-red-600 text-sm flex items-center gap-1 mt-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {fieldErrors[name]}
        </p>
      )}
      
      {/* Help text */}
      {helpText && !hasError && (
        <p className="text-[#254e70]/60 text-sm">{helpText}</p>
      )}
    </div>
  );
};

// Checkbox field component - moved outside to prevent re-creation on each render
// eslint-disable-next-line no-unused-vars
const CheckboxField = ({ label, name, helpText = '', value, onChange, isSubmitting }) => (
  <div className="flex items-start gap-3">
    <div className="relative mt-1">
      <input
        type="checkbox"
        id={name}
        name={name}
        checked={value}
        onChange={onChange}
        className="sr-only"
        disabled={isSubmitting}
      />
      <div 
        className={`w-5 h-5 border-2 rounded cursor-pointer transition-all duration-200 flex items-center justify-center ${
          value 
            ? 'bg-[#9dacff] border-[#9dacff]' 
            : 'border-gray-300 hover:border-[#9dacff]'
        }`}
        onClick={() => onChange({ target: { name, type: 'checkbox', checked: !value } })}
      >
        {value && (
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
    </div>
    <div className="flex-1">
      <label htmlFor={name} className="text-sm text-[#254e70] cursor-pointer font-medium">
        {label}
      </label>
      {helpText && (
        <p className="text-[#254e70]/60 text-xs mt-1">{helpText}</p>
      )}
    </div>
  </div>
);

function TherapistSignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    // Basic Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Practice Location
    city: '',
    state: '',
    
    // Essential Professional Info
    licenseNumber: '',
    licenseState: '',
    
    // Security
    password: '',
    confirmPassword: '',
    terms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  // Card validation state
  const [cardValidated, setCardValidated] = useState(false);
  const [, setCardError] = useState('');
  const [stripeData, setStripeData] = useState(null);
  const cardValidationRef = useRef(null);

  // Success state
  const [showSuccess, setShowSuccess] = useState(false);

  // Real-time validation
  const validateField = (name, value) => {
    const errors = {};
    
    switch (name) {
      case 'email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors.email = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (value && !/^[+]?[(]?[\d\s\-()]{10,}$/.test(value)) {
          errors.phone = 'Please enter a valid phone number';
        }
        break;
      case 'password':
        if (value && value.length < 6) {
          errors.password = 'Password must be at least 6 characters';
        } else if (value && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          errors.password = 'Password must include uppercase, lowercase, and number';
        }
        break;
      case 'confirmPassword':
        if (value && value !== formData.password) {
          errors.confirmPassword = 'Passwords do not match';
        }
        break;

      default:
        break;
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Mark field as touched
    setTouchedFields(prev => ({
      ...prev,
      [name]: true
    }));

    // Validate field in real-time
    const fieldValidationErrors = validateField(name, newValue);
    setFieldErrors(prev => ({
      ...prev,
      ...fieldValidationErrors,
      [name]: fieldValidationErrors[name] || undefined
    }));

    // Clear global error when user starts typing
    if (error) setError('');
  };

  // Card validation handlers
  const handleCardValidated = (data) => {
    setCardValidated(true);
    setStripeData(data);
    setCardError('');
  };

  const handleCardError = (errorMessage) => {
    setCardValidated(false);
    setStripeData(null);
    setCardError(errorMessage);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting || showSuccess) {
      console.log('Submission prevented - already processing or completed');
      return;
    }
    
    setIsSubmitting(true);
    setError('');

    // Mark all fields as touched for validation display
    const allFields = Object.keys(formData);
    setTouchedFields(allFields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));

    // Comprehensive validation
    const validationErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      const fieldErrors = validateField(key, value);
      Object.assign(validationErrors, fieldErrors);
    });

    // Check required fields
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'city', 'state', 'licenseNumber', 'licenseState', 'password'];
    const missingFields = requiredFields.filter(field => !formData[field]);
    
    if (missingFields.length > 0) {
      missingFields.forEach(field => {
        validationErrors[field] = 'This field is required';
      });
    }

    if (!formData.terms) {
      validationErrors.terms = 'You must accept the terms and conditions';
    }

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setError('Please correct the errors below and try again.');
      setIsSubmitting(false);
      return;
    }

    // Step 1: Validate credit card first
    if (!cardValidated || !stripeData) {
      try {
        // Trigger card validation through the ref
        if (cardValidationRef.current && cardValidationRef.current.validateCard) {
          const cardValidationSuccess = await cardValidationRef.current.validateCard();
          if (!cardValidationSuccess) {
            setError('Please provide a valid credit card to continue. Your card will not be charged at signup.');
            setIsSubmitting(false);
            return;
          }
        } else {
          setError('Credit card validation is required. Please ensure your card information is complete.');
          setIsSubmitting(false);
          return;
        }
      } catch (error) {
        console.error('Card validation error:', error);
        setError('Credit card validation failed. Please check your card information and try again.');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      console.log('Submitting registration data:', {
        email: formData.email,
        password: formData.password.length + ' characters'
      });

      // Step 2: Register the user (defaults to therapist)
      const registerResponse = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const registerData = await registerResponse.json();

      if (!registerResponse.ok) {
        console.error('Registration failed:', registerData);
        
        // Handle specific error cases with better messaging
        if (registerData.message && registerData.message.includes('already exists')) {
          throw new Error(`An account with email ${formData.email} already exists. Please use a different email or try logging in instead.`);
        }
        
        if (registerData.errors && Array.isArray(registerData.errors)) {
          const errorMessages = registerData.errors.map(err => err.msg).join(', ');
          throw new Error(`Account creation failed: ${errorMessages}`);
        }
        
        throw new Error(registerData.message || 'Account creation failed. Please try again.');
      }

      // Step 3: Create basic therapist profile (comprehensive details added later)
      const profilePayload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        
        // Practice Location
        location: {
          city: formData.city,
          state: formData.state
        },
        
        // Essential Professional Info
        professional: {
          licenseNumber: formData.licenseNumber,
          licenseState: formData.licenseState
        },

        // Stripe verification data
        verification: {
          stripeCustomerId: stripeData?.stripeCustomerId,
          paymentMethodValidated: true,
          validatedAt: stripeData?.validatedAt
        }
      };
      
      console.log('Sending basic therapist profile data:', profilePayload);
      
      const profileResponse = await fetch(`${API_BASE_URL}/api/therapists/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${registerData.token}`
        },
        body: JSON.stringify(profilePayload),
      });

      const profileData = await profileResponse.json();

      if (!profileResponse.ok) {
        console.error('Profile creation failed:', profileData);
        
        if (profileData.errors && Array.isArray(profileData.errors)) {
          const errorMessages = profileData.errors.map(err => err.msg).join(', ');
          throw new Error(`Therapist profile setup failed: ${errorMessages}`);
        }
        
        throw new Error(profileData.message || 'Therapist profile setup failed. Please contact support.');
      }

      // Success! Show success message and redirect to login
      setError(''); // Clear any errors
      setShowSuccess(true);
      console.log('✅ Account created successfully!');
      console.log('Setting up redirect to login page...');
      
      // Show success message for 2 seconds, then redirect
      setTimeout(() => {
        console.log('Redirecting to login page now...');
        navigate('/login', { 
          state: { 
            message: 'Account created successfully! Please log in to access your therapist dashboard.',
            email: formData.email 
          }
        });
      }, 2000);

      // Don't set isSubmitting to false on success - keep button disabled
      return;

    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'There was an error submitting your application. Please try again.');
      setIsSubmitting(false); // Only re-enable on error
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#e4fde1] to-[#ffffff] text-[#254e70] min-h-screen" style={{ marginTop: '-200px', paddingTop: '200px' }}>
      {/* Signup Section */}
      <section className="py-6 pb-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-white/90 backdrop-blur-sm border border-[#254e70]/10 text-[#254e70] px-3 py-1.5 rounded-full text-xs font-medium mb-4 shadow-sm">
              Join Our Licensed Professionals
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-[#254e70] mb-3 leading-tight">
              Join <span className="text-[#9dacff]">TherapyList</span>
            </h1>
            <p className="text-base text-[#254e70]/70 max-w-xl mx-auto mb-3">
              Create your account immediately and start updating your profile! We'll let you know when your profile is live!
            </p>
            <div className="flex items-center justify-center gap-2 text-sm">
              <span className="text-[#254e70]/60">Questions about our free service?</span>
              <a 
                href="#faq-section" 
                className="text-[#9dacff] hover:text-[#254e70] font-medium underline transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('faq-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See FAQ below
              </a>
            </div>
          </div>

          {/* Form Container */}
          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4 lg:p-6 shadow-2xl border border-[#254e70]/10 max-w-3xl mx-auto">
            {/* Success Message */}
            {showSuccess && (
              <div className="mb-8 p-6 bg-green-50 border-2 border-green-200 rounded-xl shadow-sm">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-green-800 font-semibold text-lg mb-2">🎉 Account Created Successfully!</h4>
                    <p className="text-green-700 leading-relaxed mb-3">
                      Your therapist account has been created and your payment method verified. 
                      You're being redirected to the login page...
                    </p>
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin"></div>
                      <span>Redirecting in a moment...</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Global Error Message */}
            {error && !showSuccess && (
              <div className="mb-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl shadow-sm">
                <div className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-red-800 font-semibold text-lg mb-2">Account Creation Failed</h4>
                    <p className="text-red-700 leading-relaxed">{error}</p>
                    {error.includes('already exists') && (
                      <div className="mt-3 p-3 bg-red-100 rounded-lg">
                        <p className="text-red-600 text-sm">
                          <strong>Already have an account?</strong> <Link to="/login" className="underline hover:text-red-800">Sign in here</Link>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className={`space-y-4 ${showSuccess ? 'pointer-events-none opacity-60' : ''}`}>
              {/* Personal Information */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-[#254e70] pb-2 border-b border-gray-100">Personal Information</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FormField
                    label="First Name"
                    name="firstName"
                    placeholder="Enter your first name"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                  <FormField
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter your last name"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FormField
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="your.email@domain.com"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                  <FormField
                    label="Phone Number"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>

                                            {/* Practice Location */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-[#254e70] pb-2 border-b border-gray-100">Practice Location</h3>
                 
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FormField
                    label="City"
                    name="city"
                    placeholder="Enter your city"
                    required
                    value={formData.city}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                  <FormField
                    label="State"
                    name="state"
                    type="select"
                    required
                    value={formData.state}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                    options={[
                      { value: 'AL', label: 'Alabama' },
                      { value: 'AK', label: 'Alaska' },
                      { value: 'AZ', label: 'Arizona' },
                      { value: 'AR', label: 'Arkansas' },
                      { value: 'CA', label: 'California' },
                      { value: 'CO', label: 'Colorado' },
                      { value: 'CT', label: 'Connecticut' },
                      { value: 'DE', label: 'Delaware' },
                      { value: 'DC', label: 'District of Columbia' },
                      { value: 'FL', label: 'Florida' },
                      { value: 'GA', label: 'Georgia' },
                      { value: 'HI', label: 'Hawaii' },
                      { value: 'ID', label: 'Idaho' },
                      { value: 'IL', label: 'Illinois' },
                      { value: 'IN', label: 'Indiana' },
                      { value: 'IA', label: 'Iowa' },
                      { value: 'KS', label: 'Kansas' },
                      { value: 'KY', label: 'Kentucky' },
                      { value: 'LA', label: 'Louisiana' },
                      { value: 'ME', label: 'Maine' },
                      { value: 'MD', label: 'Maryland' },
                      { value: 'MA', label: 'Massachusetts' },
                      { value: 'MI', label: 'Michigan' },
                      { value: 'MN', label: 'Minnesota' },
                      { value: 'MS', label: 'Mississippi' },
                      { value: 'MO', label: 'Missouri' },
                      { value: 'MT', label: 'Montana' },
                      { value: 'NE', label: 'Nebraska' },
                      { value: 'NV', label: 'Nevada' },
                      { value: 'NH', label: 'New Hampshire' },
                      { value: 'NJ', label: 'New Jersey' },
                      { value: 'NM', label: 'New Mexico' },
                      { value: 'NY', label: 'New York' },
                      { value: 'NC', label: 'North Carolina' },
                      { value: 'ND', label: 'North Dakota' },
                      { value: 'OH', label: 'Ohio' },
                      { value: 'OK', label: 'Oklahoma' },
                      { value: 'OR', label: 'Oregon' },
                      { value: 'PA', label: 'Pennsylvania' },
                      { value: 'RI', label: 'Rhode Island' },
                      { value: 'SC', label: 'South Carolina' },
                      { value: 'SD', label: 'South Dakota' },
                      { value: 'TN', label: 'Tennessee' },
                      { value: 'TX', label: 'Texas' },
                      { value: 'UT', label: 'Utah' },
                      { value: 'VT', label: 'Vermont' },
                      { value: 'VA', label: 'Virginia' },
                      { value: 'WA', label: 'Washington' },
                      { value: 'WV', label: 'West Virginia' },
                      { value: 'WI', label: 'Wisconsin' },
                      { value: 'WY', label: 'Wyoming' }
                    ]}
                  />
                </div>
              </div>

              {/* License Information */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-[#254e70] pb-2 border-b border-gray-100">License Information</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FormField
                    label="License Number"
                    name="licenseNumber"
                    placeholder="Enter your license number"
                    required
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                  <FormField
                    label="License State"
                    name="licenseState"
                    type="select"
                    required
                    value={formData.licenseState}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                    options={[
                      { value: 'AL', label: 'Alabama' },
                      { value: 'AK', label: 'Alaska' },
                      { value: 'AZ', label: 'Arizona' },
                      { value: 'AR', label: 'Arkansas' },
                      { value: 'CA', label: 'California' },
                      { value: 'CO', label: 'Colorado' },
                      { value: 'CT', label: 'Connecticut' },
                      { value: 'DE', label: 'Delaware' },
                      { value: 'DC', label: 'District of Columbia' },
                      { value: 'FL', label: 'Florida' },
                      { value: 'GA', label: 'Georgia' },
                      { value: 'HI', label: 'Hawaii' },
                      { value: 'ID', label: 'Idaho' },
                      { value: 'IL', label: 'Illinois' },
                      { value: 'IN', label: 'Indiana' },
                      { value: 'IA', label: 'Iowa' },
                      { value: 'KS', label: 'Kansas' },
                      { value: 'KY', label: 'Kentucky' },
                      { value: 'LA', label: 'Louisiana' },
                      { value: 'ME', label: 'Maine' },
                      { value: 'MD', label: 'Maryland' },
                      { value: 'MA', label: 'Massachusetts' },
                      { value: 'MI', label: 'Michigan' },
                      { value: 'MN', label: 'Minnesota' },
                      { value: 'MS', label: 'Mississippi' },
                      { value: 'MO', label: 'Missouri' },
                      { value: 'MT', label: 'Montana' },
                      { value: 'NE', label: 'Nebraska' },
                      { value: 'NV', label: 'Nevada' },
                      { value: 'NH', label: 'New Hampshire' },
                      { value: 'NJ', label: 'New Jersey' },
                      { value: 'NM', label: 'New Mexico' },
                      { value: 'NY', label: 'New York' },
                      { value: 'NC', label: 'North Carolina' },
                      { value: 'ND', label: 'North Dakota' },
                      { value: 'OH', label: 'Ohio' },
                      { value: 'OK', label: 'Oklahoma' },
                      { value: 'OR', label: 'Oregon' },
                      { value: 'PA', label: 'Pennsylvania' },
                      { value: 'RI', label: 'Rhode Island' },
                      { value: 'SC', label: 'South Carolina' },
                      { value: 'SD', label: 'South Dakota' },
                      { value: 'TN', label: 'Tennessee' },
                      { value: 'TX', label: 'Texas' },
                      { value: 'UT', label: 'Utah' },
                      { value: 'VT', label: 'Vermont' },
                      { value: 'VA', label: 'Virginia' },
                      { value: 'WA', label: 'Washington' },
                      { value: 'WV', label: 'West Virginia' },
                      { value: 'WI', label: 'Wisconsin' },
                      { value: 'WY', label: 'Wyoming' }
                    ]}
                  />
                </div>
              </div>

              {/* Account Security */}
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-[#254e70] pb-2 border-b border-gray-100">Account Security</h3>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <FormField
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="Create a strong password"
                    required
                    helpText="Must be at least 6 characters with uppercase, lowercase, and number"
                    value={formData.password}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                  <FormField
                    label="Confirm Password"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    fieldErrors={fieldErrors}
                    touchedFields={touchedFields}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>

              {/* Credit Card Verification */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-[#254e70] pb-2 border-b border-gray-100">
                    Credit Card Verification
                  </h3>
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-semibold">
                    100% FREE
                  </div>
                </div>
                
                
                <StripeCardCollection
                  validationRef={cardValidationRef}
                  onCardValidated={handleCardValidated}
                  onCardError={handleCardError}
                  isSubmitting={isSubmitting}
                  userEmail={formData.email}
                  disabled={!formData.email}
                />

                {/* Success indicator */}
                {cardValidated && stripeData && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                    <div className="flex items-center gap-2 text-green-700">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-medium">Card Verified Successfully</span>
                    </div>
                    <p className="text-green-600 text-sm mt-1">
                      {stripeData.cardInfo?.brand?.toUpperCase()} ending in {stripeData.cardInfo?.last4} • No charges applied
                    </p>
                  </div>
                )}
              </div>

              {/* Terms & Conditions */}
              <div className="bg-[#e4fde1]/30 rounded-xl p-3">
                <div className="flex items-start gap-4">
                  <div className="relative">
                    <input
                      type="checkbox"
                      id="terms"
                      name="terms"
                      checked={formData.terms}
                      onChange={handleInputChange}
                      className="sr-only"
                      required
                      disabled={isSubmitting}
                    />
                    <div 
                      className={`w-6 h-6 border-2 rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        formData.terms 
                          ? 'bg-[#9dacff] border-[#9dacff]' 
                          : touchedFields.terms && fieldErrors.terms
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 hover:border-[#9dacff]'
                      }`}
                      onClick={() => handleInputChange({ target: { name: 'terms', type: 'checkbox', checked: !formData.terms } })}
                    >
                      {formData.terms && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <label htmlFor="terms" className="text-sm text-[#254e70]/80 leading-relaxed cursor-pointer">
                      I agree to the <Link to="/terms" className="text-[#9dacff] hover:underline font-medium">Terms of Service</Link> and <Link to="/privacy" className="text-[#9dacff] hover:underline font-medium">Privacy Policy</Link>. I understand that my license will be verified before my profile goes live.
                    </label>
                    {touchedFields.terms && fieldErrors.terms && (
                      <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {fieldErrors.terms}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-3">
                {/* Free Account Reminder */}
                <div className="text-center mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-blue-800 text-sm font-medium">
                    🎉 Creating your account is completely FREE • No hidden fees • No subscription required
                  </p>
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting || showSuccess}
                  className="w-full bg-gradient-to-r from-[#254e70] to-[#1d3e5a] text-white px-8 py-5 rounded-2xl text-lg font-semibold hover:from-[#1d3e5a] hover:to-[#254e70] hover:-translate-y-1 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none relative overflow-hidden group"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Creating Your Free Account...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Create Free Account</span>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Animated background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                </button>
                
                {/* Additional reassurance below button */}
                <p className="text-center text-[#254e70]/60 text-xs mt-3">
                  ✓ No charges during signup ✓ Card verification only
                </p>
              </div>
            </form>
          </div>

          {/* What's Next Section */}
          <div className="mt-6 p-4 lg:p-5 bg-[#e4fde1]/40 backdrop-blur-sm rounded-xl border border-[#9dacff]/20 shadow-lg">
            <div className="text-center mb-4">
              <h3 className="text-lg lg:text-xl font-bold text-[#254e70] mb-2">
                What happens after you sign up?
              </h3>
              <p className="text-[#254e70]/70 text-sm">Your journey to connecting with clients</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="w-10 h-10 bg-[#9dacff] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-sm font-semibold">1</span>
                </div>
                <h4 className="font-semibold text-[#254e70] mb-1 text-sm">Complete Your Profile</h4>
                <p className="text-[#254e70]/70 text-xs">Add your bio, specializations, and rates</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-[#9dacff] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-sm font-semibold">2</span>
                </div>
                <h4 className="font-semibold text-[#254e70] mb-1 text-sm">Get Verified</h4>
                <p className="text-[#254e70]/70 text-xs">We verify your license for client safety</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 bg-[#9dacff] rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-sm font-semibold">3</span>
                </div>
                <h4 className="font-semibold text-[#254e70] mb-1 text-sm">Connect with Clients</h4>
                <p className="text-[#254e70]/70 text-xs">Your profile goes live and clients can find you</p>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div id="faq-section" className="mt-8 max-w-3xl mx-auto">
            <div className="text-center mb-6">
              <h3 className="text-xl lg:text-2xl font-bold text-[#254e70] mb-2">
                Frequently Asked Questions
              </h3>
              <p className="text-[#254e70]/70 text-sm">Everything you need to know about joining TherapyList</p>
            </div>

            <div className="space-y-4">
              {/* How is it free? */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-[#254e70]/10">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#9dacff] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#254e70] text-lg mb-2">How is TherapyList completely free?</h4>
                    <div className="text-[#254e70]/80 text-sm leading-relaxed space-y-3">
                      <p>
                        We know it might sound too good to be true. But we're offering this for free while we grow the platform and gather feedback from therapists like you.
                      </p>
                      <p>
                        <strong>There's no catch, no hidden fees.</strong> Just real visibility for your practice, at no cost.
                      </p>
                      <p>
                        Eventually, we may introduce paid features, but for now, everything is 100% free to use. Get in early and help shape what we're building.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Why credit card required? */}
              <div className="bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-[#254e70]/10">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#9dacff] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-[#254e70] text-lg mb-2">Why do you need my credit card if it's free?</h4>
                    <p className="text-[#254e70]/80 text-sm leading-relaxed mb-3">
                      We require credit card verification for several important reasons that protect both therapists and clients:
                    </p>
                    <ul className="space-y-2 text-[#254e70]/80 text-sm">
                      <li className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span><strong>Prevents fake accounts</strong> - Ensures only serious, verified professionals join</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                        </svg>
                        <span><strong>Identity verification</strong> - Confirms you are who you say you are</span>
                      </li>
                    </ul>
                    <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-green-800 text-sm font-medium flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <strong>Promise:</strong> We will never charge your card without explicit permission
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-4 text-center">
            <p className="text-[#254e70]/60 text-xs">
              Secure • Licensed Professionals Only
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TherapistSignupPage; 
